import type { ReactNode } from 'react'

interface CardProps {
  children: ReactNode
  className?: string
}

export function Card({ children, className }: CardProps) {
  return (
    <div
      className={[
        'rounded-[--radius-card] border border-[color:var(--surface-border)]',
        'bg-[color:var(--surface-raised)] shadow-soft',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {children}
    </div>
  )
}

interface CardHeaderProps {
  title: string
  description?: string
  action?: ReactNode
}

export function CardHeader({ title, description, action }: CardHeaderProps) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[color:var(--surface-border)] px-5 py-4">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        {description && <p className="mt-1 text-sm text-[color:var(--text-muted)]">{description}</p>}
      </div>
      {action}
    </div>
  )
}

export function CardBody({ children, className }: CardProps) {
  return <div className={['px-5 py-4', className].filter(Boolean).join(' ')}>{children}</div>
}

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

const tones: Record<Tone, string> = {
  neutral: 'bg-[color:var(--surface-sunken)] text-[color:var(--text-muted)]',
  success: 'bg-forest-100 text-forest-800',
  warning: 'bg-brass-100 text-brass-800',
  danger: 'bg-red-100 text-red-800',
  info: 'bg-sky-100 text-sky-800',
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: Tone }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  )
}

interface EmptyStateProps {
  title: string
  description: string
  action?: ReactNode
}

/**
 * Every list in this app starts empty for a brand new unit, so the empty state is the
 * first thing a Unit Admin sees and needs to say what to do next.
 */
export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className="px-5 py-12 text-center">
      <h3 className="text-sm font-semibold">{title}</h3>
      <p className="mx-auto mt-1 max-w-sm text-sm text-[color:var(--text-muted)]">{description}</p>
      {action && <div className="mt-5 flex justify-center">{action}</div>}
    </div>
  )
}
